import React from 'react'
import { styled } from 'styled-components'
import type {
	CurrentMissionPerformanceScores,
	PastAnalyticsPerformanceScores,
} from '@mission.io/mission-player'
import { getFormattedDate } from './helpers'
import ProficiencyChart from './ProficiencyChart'

/**
 * GraphDisplay - Display a graph of the current mission scores compared with the most recent previous mission scores
 *
 * Any updates made to this component CANNOT include useContext
 * @param  {Object} props - the react props
 * @param  {?(PastAnalyticsPerformanceScores[])} props.pastAnalyticsPerformanceScores - the scores of the past missions
 * @param  {?CurrentMissionPerformanceScores} props.currentMissionScores - the scores of the current mission
 *
 * @returns React$Node
 */
export default function GraphDisplay({
	pastAnalyticsPerformanceScores,
	currentMissionScores,
}: {
	pastAnalyticsPerformanceScores: ?(PastAnalyticsPerformanceScores[]),
	currentMissionScores: ?CurrentMissionPerformanceScores,
}): React$Node {
	if (!currentMissionScores) {
		return null
	}
	const averageScore =
		currentMissionScores.analyticsOverview?.averageStudentScore
	const finalScore = currentMissionScores.analyticsOverview?.finalScore
	return (
		<Card data-testid="most-recent-mission">
			<CardBody>
				<Row>
					<MissionInfoCard>
						<h3 className="font-game-headline">
							{currentMissionScores.className}
						</h3>
						Date: {getFormattedDate(new Date(currentMissionScores.createdTime))}
						<br />
						Mission: {currentMissionScores.missionName}
					</MissionInfoCard>
					<ScoreCard>
						<h2 className="font-game-headline">
							Mission Score: {formatPoints(finalScore)}
						</h2>
						<span>Average Student Score: {formatPoints(averageScore)}</span>
					</ScoreCard>
					<Grow />
				</Row>
				<ProficiencyChart scores={currentMissionScores.performanceScores} />
			</CardBody>
		</Card>
	)
}

/**
 * formatPoints - convert the points count to a string to be displayed to the user
 *
 * @param  {?number} pointCount - the amount of points to convert
 *
 * @returns string - the representation of the pointCount to display to the user
 */
function formatPoints(pointCount: ?number): string {
	if (pointCount == null) {
		return 'Unknown'
	}
	const roundedPoints = Math.round(pointCount)
	return roundedPoints.toLocaleString()
}

const Card = styled.div`
	background: linear-gradient(180deg, #3f3b6e 0%, #5b5889 100%);
	box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
	border-radius: 8px;
	padding: 32px;
	width: 100%;
`

const CardBody = styled.div`
	color: white;
`

const ScoreCard = styled.div`
	background-color: #3f3b6e;
	border-radius: 8px;
	padding: 20px;
	text-align: center;
	margin-bottom: 16px;
	font-size: 1.5em;
`

const MissionInfoCard = styled.div`
	text-align: left;
	font-size: 1.5em;
	flex: 1;
`

const Row = styled.div`
	display: flex;
`

const Grow = styled.div`
	flex: 1;
`
